// Merchants — admin-only list, wired to GET /v1/merchants (Phase 06.1).
//
// The backend `merchants` table has no industry / country / owner / MRR /
// balance columns — those were mock-only. This screen shows the real fields:
// display name, slug, status, created.
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { I } from "@/components/Icons";
import { Modal, StatusPill } from "@/components/UI";
import { formatDateTime } from "@/utils/format";
import { downloadCsv } from "@/utils/csv";
import { apiErrorMessage } from "@/utils/apiError";
import { useMerchants } from "@/queries/useMerchants";
import { createMerchant } from "@/api/merchants";

// ─── New merchant modal ───────────────────────────────────────────────────────

function NewMerchantModal({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function reset() {
    setSlug("");
    setDisplayName("");
    setError(null);
    setSaving(false);
  }

  function close() {
    reset();
    onClose();
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const m = await createMerchant({
        slug: slug.trim(),
        display_name: displayName.trim(),
      });
      await qc.invalidateQueries({ queryKey: ["merchants"] });
      reset();
      onClose();
      navigate(`/merchants/${m.slug || m.id}`);
    } catch (err) {
      // AUDIT-PASS-6 P-1: was a local switch on err.response.data.code with
      // hardcoded copy for slug_taken / slug_invalid / display_name_invalid.
      // Now routed through the shared apiErrorMessage; the same three codes
      // live in ERROR_COPY and the fallback path adds status-aware copy
      // (404/409/422/5xx) and network-drop copy that the local switch
      // didn't handle. One source of truth for backend → user-copy mapping.
      setError(apiErrorMessage(err, "Could not create the merchant. Try again."));
      setSaving(false);
    }
  }

  const canSubmit = !saving && slug.trim() !== "" && displayName.trim() !== "";

  return (
    <Modal
      open={open}
      onClose={close}
      busy={saving}
      title="New merchant"
      foot={
        <>
          <button
            className="btn ghost"
            type="button"
            onClick={close}
            disabled={saving}
          >
            Cancel
          </button>
          <div className="flex-1" />
          <button
            className="btn accent"
            type="submit"
            form="new-merchant-form"
            disabled={!canSubmit}
          >
            {saving ? "Creating…" : "Create merchant"}
          </button>
        </>
      }
    >
      <form id="new-merchant-form" onSubmit={handleSubmit} className="stack-16">
        <div className="field">
          <label className="label" htmlFor="nm-name">
            Display name
          </label>
          <input
            id="nm-name"
            className="input"
            type="text"
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="nm-slug">
            Slug
          </label>
          <input
            id="nm-slug"
            className="input"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="acme-corp"
          />
          <div className="dim" style={{ fontSize: 11, marginTop: 4 }}>
            Permanent identifier — lowercase letters, digits and hyphens
            (2–40 chars). Cannot be changed later.
          </div>
        </div>
        {error && (
          // AUDIT-PASS-6 P-1: was `var(--warn, #b42318)` — an amber CSS var
          // with a red literal fallback, which is semantically confused.
          // Backend rejection of a create is an error, not a caution. The
          // design system has --neg (red) for errors; matching every other
          // error surface on the panel.
          <div style={{ color: "var(--neg)", fontSize: 12 }}>{error}</div>
        )}
      </form>
    </Modal>
  );
}

export function Merchants() {
  const navigate = useNavigate();
  const { data: merchants = [], isLoading, isError } = useMerchants();
  const [newOpen, setNewOpen] = useState(false);

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <div className="eyebrow">Console · Admin</div>
          <h1>Merchants</h1>
          <div className="sub">
            {isLoading
              ? "Loading…"
              : `${merchants.length} merchant${merchants.length === 1 ? "" : "s"}`}
          </div>
        </div>
        <div className="actions">
          <button
            className="btn"
            type="button"
            onClick={() =>
              downloadCsv(
                "merchants.csv",
                merchants.map((m) => ({
                  id: m.id,
                  slug: m.slug,
                  display_name: m.display_name,
                  status: m.status,
                  created_at: m.created_at,
                  updated_at: m.updated_at,
                })),
              )
            }
          >
            <I.Download size={12} /> Export
          </button>
          <button
            className="btn accent"
            type="button"
            onClick={() => setNewOpen(true)}
          >
            <I.Plus size={12} /> New merchant
          </button>
        </div>
      </div>

      <NewMerchantModal open={newOpen} onClose={() => setNewOpen(false)} />

      <div className="card">
        {isLoading ? (
          <div className="empty">
            <div className="dim" style={{ fontSize: 13 }}>
              Loading merchants…
            </div>
          </div>
        ) : isError ? (
          <div className="empty">
            <div className="icon">
              <I.Merchants size={16} />
            </div>
            <h3>Couldn't load merchants</h3>
            <p>The request to payouts-backend failed. Try again shortly.</p>
          </div>
        ) : merchants.length === 0 ? (
          <div className="empty">
            <div className="icon">
              <I.Merchants size={16} />
            </div>
            <h3>No merchants yet</h3>
            <p>Merchants provisioned in payouts-backend will appear here.</p>
          </div>
        ) : (
          <table className="tbl">
            <thead>
              <tr>
                <th>Merchant</th>
                <th>Slug</th>
                <th>Created</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {merchants.map((m) => (
                <tr
                  key={m.id}
                  className="clickable"
                  onClick={() => navigate(`/merchants/${m.slug || m.id}`)}
                >
                  <td>
                    <div className="stack-2">
                      <div className="cell-name">{m.display_name}</div>
                      <div className="cell-id">mer_{m.id}</div>
                    </div>
                  </td>
                  <td className="mono dim">{m.slug}</td>
                  <td className="dim">{formatDateTime(m.created_at)}</td>
                  <td>
                    <StatusPill status={m.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
